import { createAsyncThunk } from '@reduxjs/toolkit';
import webSocketService from '../../services/socketService';
import {
  setConnectionStatus,
  setError,
  incrementReconnectAttempts,
  resetReconnectAttempts,
  setRunningPositionSubscription,
  clearRunningPositionSubscription,
  setNotificationSubscription,
  setOpenOrdersData,
  resetOpenOrdersData
} from './socketSlice';
import { validateOpenOrdersData, sanitizeData } from '../../utils/socketDataValidator';

/**
 * Initialize socket connection
 */
export const initializeSocketConnection = createAsyncThunk(
  'socket/initializeConnection',
  async (token, { getState, dispatch }) => {
    try {
      dispatch(setConnectionStatus('connecting'));

      // In Create React App, only REACT_APP_ prefixed vars are exposed
      const socketUrl = process.env.REACT_APP_SOCKET_API_BASE_URL;
      
      // Debug: Log available env vars (only in development)
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Environment check:', {
      //     REACT_APP_SOCKET_API_BASE_URL: process.env.REACT_APP_SOCKET_API_BASE_URL,
      //     'All REACT_APP_ vars': Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      //   });
      // }
      
      if (!socketUrl) {
        throw new Error('Socket URL not configured.');
      }

      await webSocketService.connect(socketUrl, token);
      
      dispatch(setConnectionStatus('connected'));
      dispatch(resetReconnectAttempts());
      dispatch(setError(null));

      // Check for active subscription and auto re-subscribe
      const state = getState();
      // const { subscription } = state.Socket;
// const { runningPositions } = state.Socket.subscriptions;
//       if (subscription.accountNumber && subscription.topic) {
//         await dispatch(subscribeToOpenOrders({
//           accountNumber: subscription.accountNumber,
//           planId: subscription.planId
//         }));
//       }
const { runningPositions } = state.Socket.subscriptions;

if (runningPositions.accountNumber) {
    await dispatch(
        subscribeToOpenOrders({
            accountNumber: runningPositions.accountNumber,
            planId: runningPositions.planId,
        })
    );
}

      return { success: true };
    } catch (error) {
      dispatch(setConnectionStatus('error'));
      dispatch(setError(error.message || 'Failed to connect to socket'));
      dispatch(incrementReconnectAttempts());
      throw error;
    }
  }
);

/**
 * Subscribe to open orders for an account
 */
export const subscribeToOpenOrders = createAsyncThunk(
  'socket/subscribeToOpenOrders',
  async ({ accountNumber, planId }, { dispatch, getState }) => {
    // Account validation
    if (!accountNumber || typeof accountNumber !== 'string') {
      throw new Error('Invalid account number');
    }

    if (planId !== undefined && planId !== null && (typeof planId !== 'number' || planId < 1)) {
      throw new Error('Invalid plan ID');
    }

    // Unsubscribe current if exists (topic-specific so notifications stay active)
    const state = getState();
    if (state.Socket.subscriptions.runningPositions.accountNumber) {
      webSocketService.emit('customEvent', {
        topic: 'runningPositions',
        type: 'unsubscribe'
      });
    }

    // Subscribe to new account
    const subscription = {
      topic: 'runningPositions',
      accountNumber,
      planId: planId 
    };

    webSocketService.emit('customEvent', subscription);

    // Store in Redux (single source of truth)
    dispatch(setRunningPositionSubscription({
      topic: 'runningPositions',
      accountNumber,
      planId: planId
    }));

    return { success: true };
  }
);

/**
 * Unsubscribe from open orders
 */
export const unsubscribeFromOpenOrders = createAsyncThunk(
  'socket/unsubscribeFromOpenOrders',
  async (_, { dispatch }) => {
    // Topic-specific unsubscribe — does NOT affect adminNotification or other subscriptions
    webSocketService.emit('customEvent', {
      topic: 'runningPositions',
      type: 'unsubscribe'
    });
    dispatch(clearRunningPositionSubscription());
    dispatch(resetOpenOrdersData());
    return { success: true };
  }
);

/**
 * Handle socket data
 */
export const handleSocketData = createAsyncThunk(
  'socket/handleSocketData',
  async (data, { getState, dispatch }) => {
    const state = getState();
    const subscribedAccount = state.Socket.subscriptions.runningPositions.accountNumber;

    // Essential validation: structure + account match
    const validation = validateOpenOrdersData(data, subscribedAccount);
    if (!validation.valid) {
      console.warn('Invalid socket data:', validation.error);
      return;
    }

    // Sanitize and process data
    const sanitized = sanitizeData(data);
    dispatch(setOpenOrdersData(sanitized));
  }
);

/**
 * Disconnect socket
 */
export const disconnectSocket = createAsyncThunk(
  'socket/disconnect',
  async (_, { dispatch }) => {
    webSocketService.disconnect();
    dispatch(setConnectionStatus('disconnected'));
    dispatch(clearRunningPositionSubscription());
    dispatch(resetOpenOrdersData());
    return { success: true };
  }
);

export const subscribeNotifications = createAsyncThunk(
    "socket/subscribeNotifications",
    async (_, { dispatch }) => {

        webSocketService.emit("customEvent", {
            topic: "adminNotification"
        });

        dispatch(setNotificationSubscription(true));
    }
);


export const unsubscribeNotifications = createAsyncThunk(
    "socket/unsubscribeNotifications",
    async (_, { dispatch }) => {

        webSocketService.emit("customEvent", {
            topic: "adminNotification",
            type: "unsubscribe"
        });

        dispatch(setNotificationSubscription(false));
    }
);